import React, { useState } from "react";
import { usePromptStore } from "../../store/promptStore";
import { Loader, CheckCircle, AlertCircle } from "lucide-react";

// Sample prompts from the original PromptLibrary component
const samplePrompts = [
  {
    id: "1",
    title: "Content Summarizer",
    description: "Summarize long-form content into concise key points",
    tags: ["Content", "Summarization", "Productivity"],
    category: "Content Creation",
    content: `# Content Summarizer

## Task
Summarize the following content into a concise format that highlights the key points while maintaining the core message.

## Context
I need to quickly understand the main points of this content without reading the entire text. The summary should be comprehensive enough to give me a clear understanding of the content.

## Role
Act as an expert content analyst and summarizer who specializes in distilling complex information into clear, concise summaries.

## Example
Original: [Paste your content here]

Summary:
**TL;DR:** [One-sentence summary]

Key points:
• [First key point]
• [Second key point]
• [Third key point]

Important details:
• [Important statistic or data point]
• [Notable quote]
• [Significant conclusion]

## Instructions
- Create a summary that is approximately 20% of the original length
- Maintain the original tone and perspective
- Highlight the 3-5 most important points
- Preserve key statistics, data points, and quotes
- Organize the summary with bullet points or numbered lists for readability
- Include a one-sentence TL;DR at the beginning`,
  },
  {
    id: "2",
    title: "Email Response Generator",
    description: "Generate professional email responses for various situations",
    tags: ["Email", "Communication", "Professional"],
    category: "Communication",
    content: `# Email Response Generator

## Task
Generate a professional email response to the message provided below.

## Context
I need to respond to this email in a professional manner that addresses all points raised while maintaining appropriate tone and brevity.

## Role
Act as an experienced executive assistant who specializes in professional communication and email management.

## Example
Original email:
[Paste the email you received]

Response:
Subject: [Clear, concise subject line]

Dear [Name],

[Opening acknowledgment]

[Response to main points/questions]

[Any necessary follow-up questions or clarifications]

[Closing with clear next steps if applicable]

Best regards,
[Your name]
[Your position/company]
[Contact information if needed]

## Instructions
- Maintain a professional and courteous tone
- Address all questions or points raised in the original email
- Keep the response concise (150-250 words)
- Include a proper greeting and sign-off
- Avoid unnecessary jargon or overly formal language
- Ensure the response is actionable and clear
- Match the level of formality in the original email`,
  },
  {
    id: "3",
    title: "Product Description Writer",
    description: "Create compelling product descriptions for e-commerce",
    tags: ["Marketing", "E-commerce", "Copywriting"],
    category: "Content Creation",
    content: `# Product Description Writer

## Task
Create a compelling and informative product description for the item specified below.

## Context
This product description will be used on an e-commerce website to inform potential customers and drive sales. It needs to be both informative and persuasive.

## Role
Act as an experienced e-commerce copywriter who specializes in creating product descriptions that convert browsers into buyers.

## Example
Product: [Product name and basic details]

[Attention-grabbing headline]

[Opening paragraph that introduces the product and its primary benefit]

Key Features:
• [Feature 1 and its benefit]
• [Feature 2 and its benefit]
• [Feature 3 and its benefit]

Technical Specifications:
• [Specification 1]
• [Specification 2]
• [Specification 3]

[Closing paragraph with emotional appeal]

[Call to action]

## Instructions
- Write 100-150 words that highlight the product's key features and benefits
- Use sensory language that helps customers imagine using the product
- Include specific technical specifications where relevant
- Address potential customer pain points and how this product solves them
- Incorporate SEO-friendly keywords naturally throughout the text
- Use bullet points for key features to improve scannability
- End with a clear call-to-action`,
  },
  {
    id: "4",
    title: "Social Media Post Generator",
    description: "Create engaging social media content for multiple platforms",
    tags: ["Social Media", "Marketing", "Content Creation"],
    category: "Content Creation",
    content: `# Social Media Post Generator

## Task
Create engaging social media posts for the specified topic that are optimized for different platforms.

## Context
I need to create content for multiple social media platforms to promote a specific topic, product, or event. The content should be platform-appropriate and designed to maximize engagement.

## Role
Act as a social media marketing specialist who understands the nuances of different platforms and knows how to create content that drives engagement.

## Example
Topic: [Describe your topic, product, or event]

Twitter:
[Attention-grabbing statement] [Key benefit or feature] [Link] [1-2 hashtags]

LinkedIn:
[Professional greeting]

[Paragraph 1: Introduction to topic with industry relevance]

[Paragraph 2: Details and benefits]

[Paragraph 3: Call-to-action with clear next steps]

[1-3 relevant hashtags]

Instagram:
[Engaging opening with emoji] ✨

[Descriptive text about what the image would show]

[Key points with emojis as bullet points]
• [Point 1] 🔥
• [Point 2] 💯
• [Point 3] 🙌

[Call-to-action]

[Line break]

[3-5 hashtags]

## Instructions
- Create 3 versions of the post: one each for Twitter, LinkedIn, and Instagram
- Each post should be optimized for its platform (length, tone, hashtag usage)
- Include relevant hashtags (3-5 for Instagram, 1-2 for Twitter, 1-3 for LinkedIn)
- Twitter post should be concise and attention-grabbing (under 280 characters)
- LinkedIn post should be more professional and detailed (150-250 words)
- Instagram post should be visually descriptive with emoji usage
- Include a clear call-to-action in each post
- Maintain brand voice while adapting to each platform`,
  },
  {
    id: "5",
    title: "Blog Post Outline Creator",
    description: "Generate structured outlines for blog posts on any topic",
    tags: ["Content Creation", "Blogging", "SEO"],
    category: "Content Creation",
    content: `# Blog Post Outline Creator

## Task
Create a comprehensive outline for a blog post on the specified topic.

## Context
I need to write a well-structured blog post that provides value to readers and performs well in search engines. A detailed outline will help me organize my thoughts and ensure I cover all important aspects of the topic.

## Role
Act as a content strategist and SEO specialist who knows how to structure blog content for maximum reader engagement and search visibility.

## Example
Topic: [Your blog topic]

# [Suggested SEO-Optimized Title]

## Introduction
- Hook: [Attention-grabbing statistic or question]
- Problem statement: [Describe the challenge readers face]
- Promise: [What readers will learn from this post]
- Credibility: [Why they should trust this information]

## [Main Section 1: Foundational Concept]
### [Subsection 1.1]
- [Key point to cover]
- [Example or case study to include]
### [Subsection 1.2]
- [Key point to cover]
- [Data or statistic to reference]

## [Main Section 2: Key Strategy/Technique]
### [Subsection 2.1]
- [Key point to cover]
- [Practical application]
### [Subsection 2.2]
- [Key point to cover]
- [Common mistake to avoid]

## [Main Section 3: Advanced Approach]
### [Subsection 3.1]
- [Key point to cover]
- [Expert tip or insight]

## [Main Section 4: Implementation Steps]
### [Subsection 4.1]
- [Step 1 details]
### [Subsection 4.2]
- [Step 2 details]

## Frequently Asked Questions
- [Question 1]?
- [Question 2]?
- [Question 3]?

## Conclusion
- Summary of key points
- Final insight or perspective
- Call to action: [Suggestion for reader's next step]

## Instructions
- Create a detailed outline with H2 and H3 subheadings
- Include an introduction section that hooks the reader
- Provide 4-6 main sections (H2 headings) that comprehensively cover the topic
- Each main section should have 2-4 subsections (H3 headings)
- Include a section for common questions/FAQs (3-5 questions)
- Add notes about what to include in each section (key points, examples, data)
- Suggest a compelling title with SEO value
- Include a conclusion section that summarizes key points
- Recommend 1-2 call-to-action ideas for the end of the post`,
  },
  {
    id: "6",
    title: "Code Explainer",
    description: "Get clear explanations of complex code snippets",
    tags: ["Programming", "Education", "Technical"],
    category: "Development",
    content: `# Code Explainer

## Task
Explain the provided code snippet in a clear, educational manner that helps me understand what it does and how it works.

## Context
I need to understand this code for learning purposes, debugging, or to modify it for my own project. I may not be familiar with all aspects of the language or framework used.

## Role
Act as an experienced software developer and coding instructor who can break down complex code into understandable components.

## Example
Code:
\`\`\`
[Paste your code here]
\`\`\`

Explanation:

**Overview:**
This code [brief summary of overall purpose]

**Breaking it down:**

1. **Initialization Section:**
   \`\`\`
   [Relevant code snippet]
   \`\`\`
   This part [explanation of what this section does]

2. **Main Functionality:**
   \`\`\`
   [Relevant code snippet]
   \`\`\`
   Here, the code [explanation of core functionality]

3. **Error Handling:**
   \`\`\`
   [Relevant code snippet]
   \`\`\`
   This section [explanation of error handling approach]

**Key Concepts:**
- [Concept 1]: [Brief explanation]
- [Concept 2]: [Brief explanation]

**Potential Improvements:**
- [Suggestion 1]
- [Suggestion 2]

## Instructions
- Provide a high-level summary of what the code does
- Break down the code into logical sections
- Explain each section's purpose and functionality
- Identify key programming concepts used
- Highlight any patterns, best practices, or potential issues
- Use plain language with technical terms explained when necessary
- Include comments about the code's efficiency or alternative approaches
- If applicable, suggest improvements or optimizations`,
  },
  {
    id: "7",
    title: "Meeting Agenda Creator",
    description: "Generate structured meeting agendas with time allocations",
    tags: ["Productivity", "Business", "Organization"],
    category: "Strategy & Planning",
    content: `# Meeting Agenda Creator

## Task
Create a detailed meeting agenda with appropriate time allocations for each item.

## Context
I need to organize an effective meeting that stays on track, covers all necessary topics, and respects participants' time. The agenda will be shared with all attendees before the meeting.

## Role
Act as an experienced executive assistant and meeting facilitator who knows how to structure meetings for maximum productivity.

## Example
Meeting Purpose: [Brief description of meeting purpose]
Duration: [Total meeting time]
Participants: [List of attendees]

# [Meeting Title] Agenda
**Date:** [Date]
**Time:** [Start time] - [End time]
**Location:** [Physical location or virtual platform details]
**Facilitator:** [Name]
**Note-taker:** [Name]

## Pre-meeting Preparation
- [Document to review]
- [Task to complete]
- [Information to gather]

## Agenda Items

### 1. Welcome and Introductions (5 minutes)
- Brief welcome
- Quick round of introductions (if needed)
- Review meeting objectives

### 2. [Topic 1] (15 minutes)
- [Specific points to cover]
- [Decisions needed]
- [Owner: Name]

### 3. [Topic 2] (20 minutes)
- [Specific points to cover]
- [Decisions needed]
- [Owner: Name]

### 4. [Topic 3] (15 minutes)
- [Specific points to cover]
- [Decisions needed]
- [Owner: Name]

### 5. Next Steps and Action Items (5 minutes)
- Review decisions made
- Assign action items and deadlines
- Confirm next meeting date (if applicable)

## Post-meeting Follow-up
- Meeting notes to be distributed by: [Date]
- Next meeting: [Date and time if known]

## Instructions
- Create a professional meeting agenda with clear sections
- Include a meeting title, date, time, location/platform, and expected duration
- List all participants and their roles (if provided)
- Allocate appropriate time for each agenda item
- Include brief descriptions of what each agenda item will cover
- Add preparation requirements or pre-reading for participants
- Include time for introductions and wrap-up/next steps
- Format the agenda in a clean, professional layout
- Total time should match the specified meeting duration`,
  },
  {
    id: "8",
    title: "SWOT Analysis Generator",
    description:
      "Create a comprehensive SWOT analysis for any business or project",
    tags: ["Business", "Strategy", "Analysis"],
    category: "Analysis & Research",
    content: `# SWOT Analysis Generator

## Task
Create a comprehensive SWOT (Strengths, Weaknesses, Opportunities, Threats) analysis for the specified business, product, or project.

## Context
I need to evaluate the current position of this business/product/project and identify strategic considerations for future planning. This SWOT analysis will be used for decision-making and strategic planning.

## Role
Act as a strategic business consultant with expertise in competitive analysis and business strategy development.

## Example
# SWOT Analysis: [Business/Product/Project Name]

## Strengths (Internal, Positive)
• [Specific strength 1]
• [Specific strength 2]
• [Specific strength 3]
• [Specific strength 4]
• [Specific strength 5]

## Weaknesses (Internal, Negative)
• [Specific weakness 1]
• [Specific weakness 2]
• [Specific weakness 3]
• [Specific weakness 4]
• [Specific weakness 5]

## Opportunities (External, Positive)
• [Specific opportunity 1]
• [Specific opportunity 2]
• [Specific opportunity 3]
• [Specific opportunity 4]
• [Specific opportunity 5]

## Threats (External, Negative)
• [Specific threat 1]
• [Specific threat 2]
• [Specific threat 3]
• [Specific threat 4]
• [Specific threat 5]

## Strategic Implications
Based on this SWOT analysis, the key strategic considerations are:

1. [Strategic implication 1]
2. [Strategic implication 2]
3. [Strategic implication 3]

## Instructions
- Create a well-structured SWOT analysis with clear sections
- Include 5-7 bullet points for each SWOT category
- Each point should be concise but specific (not generic)
- Strengths and Weaknesses should focus on internal factors
- Opportunities and Threats should focus on external factors
- Provide brief explanations for each point when helpful
- Ensure points are relevant to the specific business/industry
- Include both operational and market-focused considerations
- Avoid overlapping points between categories
- Conclude with 3-5 strategic implications based on the analysis`,
  },
  {
    id: "9",
    title: "Customer Persona Creator",
    description:
      "Develop detailed customer personas for marketing and product development",
    tags: ["Marketing", "UX Research", "Business Strategy"],
    category: "Strategy & Planning",
    content: `# Customer Persona Creator

## Task
Create a detailed customer persona based on the target audience information provided.

## Context
I need to develop a comprehensive customer persona to guide our marketing, product development, and customer service strategies. This persona will help our team better understand and address the needs of our target audience.

## Role
Act as a market research analyst and customer experience specialist who excels at synthesizing demographic and psychographic data into actionable customer profiles.

## Example
# [Persona Name]

## Demographics
- Age: [Age or age range]
- Gender: [Gender]
- Location: [Urban/Suburban/Rural and region]
- Education: [Highest level of education]
- Occupation: [Job title and industry]
- Income Level: [Income range]
- Family Status: [Marital status, children]

## Quote
"[A first-person statement that captures their primary motivation or challenge]"

## Goals and Values
- [Primary goal]
- [Secondary goal]
- [Key value 1]
- [Key value 2]

## Challenges and Pain Points
- [Main challenge]
- [Secondary challenge]
- [Pain point 1]
- [Pain point 2]

## Buying Behavior
- Research process: [How they research purchases]
- Decision factors: [What influences their buying decisions]
- Price sensitivity: [High/Medium/Low]
- Brand loyalty: [High/Medium/Low]

## Technology and Media
- Tech-savviness: [Level of comfort with technology]
- Devices: [Primary devices used]
- Social media: [Platforms used and frequency]
- Content consumption: [Preferred content types and channels]

## A Day in the Life
[Brief paragraph describing a typical day for this persona, highlighting moments where your product/service could add value]

## How We Can Help
- [Specific way your product/service addresses their needs]
- [Another specific benefit for this persona]
- [How to best communicate with this persona]

## Instructions
- Create a realistic, detailed persona with a name and basic demographics
- Include both demographic and psychographic information
- Add a personal quote that captures their primary motivation
- Detail their goals, challenges, and pain points
- Describe their typical buying behavior and decision process
- Include their preferred communication channels and media consumption
- Outline their relationship with technology
- Add day-in-the-life details that make the persona realistic
- Include relevant objections or concerns they might have about products like yours
- Keep the persona focused and specific rather than trying to appeal to everyone`,
  },
  {
    id: "10",
    title: "Research Question Generator",
    description:
      "Generate focused research questions for academic or business projects",
    tags: ["Academic", "Research", "Business Intelligence"],
    category: "Analysis & Research",
    content: `# Research Question Generator

## Task
Generate a set of focused, well-structured research questions on the specified topic.

## Context
I need to develop research questions for a project that will guide data collection and analysis. These questions should be specific enough to be answerable but broad enough to yield meaningful insights.

## Role
Act as a research methodologist with expertise in designing studies and formulating research questions across multiple disciplines.

## Example
# Research Questions: [Topic]

## Primary Research Question
**Question:** [Main overarching research question]
**Type:** [Question type]
**Data Needed:** [Types of data required]
**Rationale:** [Why this question is important]

## Secondary Research Questions

### 1. [Descriptive Question]
**Type:** Descriptive
**Data Needed:** [Types of data required]
**Rationale:** [How this helps address the primary question]

### 2. [Comparative Question]
**Type:** Comparative
**Data Needed:** [Types of data required]
**Rationale:** [How this helps address the primary question]

### 3. [Relationship Question]
**Type:** Relationship/Correlational
**Data Needed:** [Types of data required]
**Rationale:** [How this helps address the primary question]

### 4. [Causal Question]
**Type:** Causal
**Data Needed:** [Types of data required]
**Rationale:** [How this helps address the primary question]

### 5. [Exploratory Question]
**Type:** Exploratory
**Data Needed:** [Types of data required]
**Rationale:** [How this helps address the primary question]

## Methodological Considerations
- [Important consideration about research approach]
- [Potential limitation to be aware of]
- [Ethical consideration if applicable]

## Instructions
- Create 5-7 research questions of varying types (descriptive, comparative, relationship-based)
- Ensure questions are clear, specific, and answerable
- Include a mix of qualitative and quantitative approaches
- Structure questions to avoid bias or leading language
- Arrange questions from broader to more specific
- For each question, include:
  - The type of question (descriptive, comparative, etc.)
  - What kind of data would be needed to answer it
  - A brief rationale for why this question is valuable
- Ensure questions are ethically sound and feasible to research
- Align questions with the stated research objectives`,
  },
];

export default function ImportPrompts() {
  const {
    createCategory,
    createTag,
    createPrompt,
    fetchCategories,
    fetchTags,
    fetchPrompts,
  } = usePromptStore();

  const [importing, setImporting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const handleImport = async () => {
    try {
      setImporting(true);
      setError(null);
      setSuccess(false);
      setProgress(0);

      // Step 1: Fetch existing categories and tags
      await fetchCategories();
      await fetchTags();
      setProgress(10);

      // Get current categories and tags
      const { categories, tags } = usePromptStore.getState();

      // Step 2: Create categories that don't exist
      const uniqueCategories = [
        ...new Set(samplePrompts.map((p) => p.category)),
      ];
      const categoryMap: Record<string, string> = {};

      for (const categoryName of uniqueCategories) {
        // Check if category already exists
        const existingCategory = categories.find(
          (c) => c.name === categoryName,
        );

        if (existingCategory) {
          categoryMap[categoryName] = existingCategory.id;
        } else {
          // Create new category
          const slug = categoryName.toLowerCase().replace(/\s+/g, "-");
          const categoryId = await createCategory({
            name: categoryName,
            slug,
            description: `Prompts related to ${categoryName}`,
          });
          categoryMap[categoryName] = categoryId;
        }
      }

      setProgress(30);

      // Step 3: Create tags that don't exist
      const allTags = samplePrompts.flatMap((p) => p.tags);
      const uniqueTags = [...new Set(allTags)];
      const tagMap: Record<string, string> = {};

      for (const tagName of uniqueTags) {
        // Check if tag already exists
        const existingTag = tags.find((t) => t.name === tagName);

        if (existingTag) {
          tagMap[tagName] = existingTag.id;
        } else {
          // Create new tag
          const slug = tagName.toLowerCase().replace(/\s+/g, "-");
          const tagId = await createTag({
            name: tagName,
            slug,
          });
          tagMap[tagName] = tagId;
        }
      }

      setProgress(50);

      // Step 4: Create prompts
      for (let i = 0; i < samplePrompts.length; i++) {
        const prompt = samplePrompts[i];
        const promptTags = prompt.tags.map((tag) => tagMap[tag]);

        await createPrompt({
          title: prompt.title,
          description: prompt.description,
          content: prompt.content,
          category_id: categoryMap[prompt.category],
          is_featured: false,
          is_published: true,
          deleted: false,
          tags: promptTags,
        });

        setProgress(50 + Math.floor(((i + 1) / samplePrompts.length) * 50));
      }

      // Step 5: Refresh data
      await fetchPrompts();

      setSuccess(true);
      setImporting(false);
    } catch (err) {
      console.error("Error importing prompts:", err);
      setError(err instanceof Error ? err.message : "Failed to import prompts");
      setImporting(false);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Import Sample Prompts
        </h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>
            Import the sample prompts from the original Prompt Library. This
            will create {samplePrompts.length} prompts with their associated
            categories and tags.
          </p>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  Successfully imported {samplePrompts.length} prompts!
                </p>
              </div>
            </div>
          </div>
        )}

        {importing && (
          <div className="mt-4">
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-[#F98B3D] bg-orange-200">
                    Progress
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-[#F98B3D]">
                    {progress}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-orange-200">
                <div
                  style={{ width: `${progress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#F98B3D]"
                ></div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-5">
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || success}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
              importing || success
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-[#F98B3D] hover:bg-[#e07a2c]"
            }`}
          >
            {importing ? (
              <>
                <Loader className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />
                Importing...
              </>
            ) : success ? (
              <>
                <CheckCircle className="-ml-1 mr-2 h-5 w-5 text-white" />
                Imported
              </>
            ) : (
              "Import Sample Prompts"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
